/**
 * L'AVENTURE DE LA SEMAINE ET SON CLASSEMENT (§8.1, design §3).
 *
 * C'est le seul écran du jeu où la question « et les autres, ils en sont où ? » a un sens : tout le
 * monde joue la même aventure, tirée d'une graine commune, et l'on peut donc comparer deux
 * parcours. Ailleurs, deux joueurs ne jouent jamais la même chose.
 *
 * LE CLASSEMENT EST UN REGISTRE, PAS UN TABLEAU DE SCORES. Une colonne de noms, l'archétype, le
 * temps de jeu, et pour les morts l'endroit où ça s'est arrêté — dans la même typographie que le
 * cimetière, parce que c'est la même chose. Pas de médailles, pas de podium, pas d'or sur les trois
 * premiers : un jeu où l'on meurt pour de bon n'a pas besoin de féliciter, il a besoin de montrer
 * ce qui est arrivé.
 *
 * ET UN JOUEUR SANS ABONNEMENT VOIT SA PLACE. L'écran le dit dans ces termes-là plutôt que de faire
 * disparaître la section : un classement absent se lit comme un classement où l'on n'est pas.
 */
import { useRessource } from "../api/chargement.js";
import type { LaSemaine, PlaceAuClassement } from "../api/types.js";
import { Glyphe } from "../composants/Glyphe.js";
import { Sceau } from "../composants/Sceau.js";
import { Filet, Parchemin } from "../composants/Parchemin.js";
import { ARCHETYPES, GENRES, dureeDite } from "../lexique/mots.js";
import { Lien } from "../routeur.js";
import { useSession } from "../session.js";
import { t, tn } from "../lexique/langue.js";

/** « 2026-W35 » devient « semaine 35 de 2026 ». La clef est faite pour un `psql`, pas pour un
 * lecteur — et c'est bien qu'elle le reste : ce qui se lit dans une base et ce qui se lit à l'écran
 * n'ont pas les mêmes contraintes. */
export function semaineDite(clef: string): string {
  const [annee, semaine] = clef.split("-W");
  if (!annee || !semaine) return clef;
  return t("semaine {semaine} de {annee}", { semaine: Number(semaine), annee });
}

function Place({ place, rang }: { place: PlaceAuClassement; rang: number | null }) {
  const mort = place.issue === "morte";
  return (
    <li className={mort ? "place place--tombe" : "place"}>
      {/* LE RANG N'EST PAS UNE MÉDAILLE : un nombre, dans la même encre que le reste. Et les morts
          n'en ont pas — ils ne courent plus. */}
      <span className="place__rang nombre">{rang !== null ? rang : <Glyphe nom="crane" taille={14} couleur="var(--sang)" />}</span>
      <span className="place__nom">{place.heros}</span>
      <span className="capitales faible">{ARCHETYPES[place.archetype as keyof typeof ARCHETYPES]?.nom ?? place.archetype}</span>
      <span className="place__quoi">
        {mort ? (
          <>
            {place.ou ? t("Tombé — {ou}", { ou: place.ou }) : t("Tombé")}{" "}
            <span className="faible">{t("après {duree}", { duree: dureeDite(place.heures) })}</span>
          </>
        ) : (
          <>
            {t("{duree} de jeu", { duree: dureeDite(place.heures) })}
            {place.reprises > 0 ? (
              <span className="faible"> · {tn(place.reprises, "{n} reprise", "{n} reprises")}</span>
            ) : null}
          </>
        )}
      </span>
    </li>
  );
}

export function Semaine() {
  const { compte } = useSession();
  const { donnees, enCours } = useRessource<LaSemaine>("/public/semaine", (charge: LaSemaine) => charge);

  if (enCours && !donnees) return <div className="page" aria-busy="true" />;
  const semaine = donnees;

  // Les vivants sont numérotés, les morts non : ils ferment la marche, et un rang sur un mort
  // laisserait croire qu'il court encore.
  let rang = 0;

  return (
    <div className="page page--etroite arrivee">
      <header className="entete-decran entete-decran--centre">
        <Sceau empreinte="chronique" taille={64} titre={t("Sceau de la semaine")} />
        <h1>{t("L'aventure de la semaine")}</h1>
        <p className="douce">
          {semaine ? semaineDite(semaine.semaine) : ""} {t("— la même pour tout le monde, tirée d'une graine commune. C'est le seul endroit où deux joueurs traversent exactement le même monde.")}
        </p>
      </header>

      {semaine?.aventure ? (
        <Parchemin variante="leve" titre={semaine.aventure.titre}>
          <p className="capitales faible">
            {GENRES[semaine.aventure.genre] ?? semaine.aventure.genre} · {tn(semaine.aventure.noeuds, "{n} nœud", "{n} nœuds")}
          </p>
          <p>
            {t("Elle ne coûte rien et ne compte dans aucun quota. Terminez-la — ou tombez en chemin, ce qui se lit aussi.")}
          </p>
          <Lien vers="/aventures" className="bouton bouton--grand">
            {t("L'ouvrir au catalogue")}
          </Lien>
        </Parchemin>
      ) : (
        <Parchemin variante="creux">
          <p className="douce">
            {t("Elle s'écrit. Aucun écran d'attente à surveiller : revenez quand vous voulez, elle sera là — et elle sera la même que pour tout le monde.")}
          </p>
        </Parchemin>
      )}

      {semaine?.maPlace ? (
        <div style={{ marginTop: "var(--e6)" }}>
          <Filet>{t("Votre parcours")}</Filet>
          <ul className="classement" style={{ marginTop: "var(--e4)" }}>
            <Place place={semaine.maPlace} rang={null} />
          </ul>
        </div>
      ) : null}

      <div style={{ marginTop: "var(--e7)" }}>
        <Filet>{t("Le registre de la semaine")}</Filet>
      </div>

      {!compte ? (
        <p className="douce" style={{ marginTop: "var(--e4)" }}>
          {t("Ouvrez un compte pour y figurer. L'aventure, elle, est gratuite.")}
        </p>
      ) : !semaine?.classementOuvert ? (
        // « VOUS N'Y AVEZ PAS DROIT » ET « VOUS N'EXISTEZ PAS » SONT DEUX PHRASES DIFFÉRENTES.
        // On inscrit tout le monde ; c'est la liste qui demande un abonnement (§8.1).
        <p className="mention mention--verrou" style={{ marginTop: "var(--e4)" }}>
          <Glyphe nom="verrou" taille={14} /> {t("Vous êtes inscrit au registre — votre parcours y entre comme celui des autres. La liste, elle, se lit avec un abonnement.")}{" "}
          <Lien vers="/souscription">{t("Les formules sont ici")}</Lien>.
        </p>
      ) : semaine.classement.length ? (
        <ul className="classement" style={{ marginTop: "var(--e4)" }}>
          {semaine.classement.map((place, index) => (
            <Place key={`${place.heros}-${index}`} place={place} rang={place.issue === "terminee" ? ++rang : null} />
          ))}
        </ul>
      ) : (
        <p className="douce" style={{ marginTop: "var(--e4)" }}>
          {t("Personne n'est encore allé au bout, ni tombé en chemin. Ça ne durera pas.")}
        </p>
      )}
    </div>
  );
}
